import { assert, BinarySearch, clamp, Listeners, Option, UUID } from "@opendaw/lib-std";
import { IndexComparator } from "./IndexComparator";
export class IndexedBoxAdapterCollection {
    static create(field, provider, pointers) {
        return new IndexedBoxAdapterCollection(field, provider, pointers);
    }
    #field;
    #entries;
    #listeners;
    #subscription;
    #sorted = null;
    constructor(field, provider, pointers) {
        this.#field = field;
        this.#entries = UUID.newSet(entry => entry.adapter.uuid);
        this.#listeners = new Listeners();
        this.#subscription = field.pointerHub.catchupAndSubscribe({
            onAdded: (pointer) => {
                this.#sorted = null;
                const adapter = provider(pointer.box);
                const subscription = adapter.indexField.subscribe(() => {
                    this.#sorted = null;
                    this.#listeners.proxy.onReorder(adapter);
                });
                const added = this.#entries.add({ adapter, subscription });
                assert(added, `Could not add ${adapter}`);
                this.#listeners.proxy.onAdd(adapter);
            },
            onRemoved: (pointer) => {
                this.#sorted = null;
                const uuid = pointer.box.address.uuid;
                const { adapter, subscription } = this.#entries.removeByKey(uuid);
                subscription.terminate();
                this.#listeners.proxy.onRemove(adapter);
            }
        }, pointers);
    }
    field() { return this.#field; }
    subscribe(listener) {
        return this.#listeners.subscribe(listener);
    }
    catchupAndSubscribe(listener) {
        this.#entries.forEach(({ adapter }) => listener.onAdd(adapter));
        return this.subscribe(listener);
    }
    getAdapterByIndex(index) {
        const idx = BinarySearch.exactMapped(this.adapters(), index, IndexComparator, adapter => adapter.indexField.getValue());
        return idx === -1 ? Option.None : Option.wrap(this.adapters()[idx]);
    }
    getAdapterById(uuid) { return this.#entries.opt(uuid).map(({ adapter }) => adapter); }
    getMinFreeIndex() {
        const adapters = this.adapters();
        for (let index = 0; index < adapters.length; index++) {
            if (adapters[index].indexField.getValue() > index) {
                return index;
            }
        }
        return adapters.length;
    }
    adapters() {
        if (this.#sorted === null) {
            this.#sorted = this.#entries.values()
                .map(({ adapter }) => adapter)
                .sort((a, b) => a.indexField.getValue() - b.indexField.getValue());
        }
        return this.#sorted;
    }
    move(adapter, delta) {
        this.moveIndex(adapter.indexField.getValue(), delta);
    }
    moveIndex(startIndex, delta) {
        const adapters = this.adapters();
        const adapter = adapters[startIndex];
        if (delta < 0) {
            const newIndex = clamp(startIndex + delta, 0, adapters.length - 1);
            for (let index = newIndex; index < startIndex; index++) {
                adapters[index].indexField.setValue(index + 1);
            }
            adapter.indexField.setValue(newIndex);
        }
        else if (delta > 1) {
            const newIndex = clamp(startIndex + (delta - 1), 0, adapters.length - 1);
            for (let index = startIndex; index < newIndex; index++) {
                adapters[index + 1].indexField.setValue(index);
            }
            adapter.indexField.setValue(newIndex);
        }
        else {
            console.warn(`moveIndex had no effect: startIndex: ${startIndex}, delta: ${delta}`);
        }
    }
    size() { return this.#entries.size(); }
    isEmpty() { return this.size() === 0; }
    terminate() {
        this.#sorted = null;
        this.#entries.forEach(({ subscription }) => subscription.terminate());
        this.#entries.clear();
        this.#listeners.terminate();
        this.#subscription.terminate();
    }
}
